/**
 * KPI table arithmetic and field coercion.
 *
 * Separate from `KpiTable.tsx` so that file exports only a component (fast
 * refresh), and so the arithmetic is testable without rendering a table. The
 * progress calculation is the part worth testing hardest: it is the one fact
 * the KPI group exists to communicate, and it has three edge cases that all
 * look fine on screen while being wrong.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */
import type { PilotKpi } from '@nexus/contracts';

/**
 * A row being edited.
 *
 * Figures are strings so a half-typed number survives a re-render — parsing on
 * every keystroke would turn "1." into 1 and move the caret.
 *
 * `key` is a client-side identity that exists for React's benefit: a new row
 * has no server id, and using the array index as a key makes React reuse the
 * wrong input's DOM node when a row is removed from the middle, so the text
 * jumps between fields.
 */
export interface KpiDraft {
  key: string;
  metricName: string;
  unit: string;
  baseline: string;
  target: string;
  actual: string;
}

/** Turns a saved KPI into an editable draft. */
export function toDraft(kpi: PilotKpi): KpiDraft {
  return {
    key: kpi.id,
    metricName: kpi.metricName,
    unit: kpi.unit ?? '',
    baseline: numberToField(kpi.baseline),
    target: numberToField(kpi.target),
    actual: numberToField(kpi.actual),
  };
}

/**
 * An empty row, for the add button.
 *
 * `crypto.randomUUID` for the key rather than a counter: a counter reset by a
 * cancelled edit would collide with a row still mounted from the previous one.
 */
export function emptyDraft(): KpiDraft {
  return {
    key: crypto.randomUUID(),
    metricName: '',
    unit: '',
    baseline: '',
    target: '',
    actual: '',
  };
}

/**
 * Renders a nullable figure into a text field's value.
 *
 * Null becomes empty rather than "0": a KPI with no actual has not been
 * measured, and showing zero would assert a measurement of nothing.
 */
export function numberToField(value: number | null): string {
  return value === null ? '' : String(value);
}

/**
 * Parses a field back to a number, or undefined.
 *
 * Undefined, not null: the API's schema treats an absent key as "no value", and
 * a blank field means the user has not supplied one. Half-typed input that does
 * not parse is also undefined rather than being sent — sending `NaN` would 400
 * with a message about a type the user never chose.
 */
export function fieldToNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Turns the drafts into the API's KPI payload.
 *
 * Rows with no metric name are dropped rather than rejected: an empty row is
 * what the Add button produces, and someone who adds one and changes their mind
 * should not have to find and remove it before the table will save.
 */
export function draftsToPayload(drafts: readonly KpiDraft[]): {
  metricName: string;
  unit?: string;
  baseline?: number;
  target?: number;
  actual?: number;
}[] {
  return drafts
    .filter((draft) => draft.metricName.trim() !== '')
    .map((draft) => {
      const unit = draft.unit.trim();
      const baseline = fieldToNumber(draft.baseline);
      const target = fieldToNumber(draft.target);
      const actual = fieldToNumber(draft.actual);

      return {
        metricName: draft.metricName.trim(),
        ...(unit === '' ? {} : { unit }),
        ...(baseline === undefined ? {} : { baseline }),
        ...(target === undefined ? {} : { target }),
        ...(actual === undefined ? {} : { actual }),
      };
    });
}

/**
 * How far an actual has come from its baseline towards its target.
 *
 * Returns null when the comparison is undefined — no target, no actual, or a
 * target equal to the baseline, which would divide by zero.
 *
 * Measured as distance travelled, not as a raw ratio, so a **decreasing**
 * target works: for a cost baseline of 100 and a target of 50, an actual of 75
 * is halfway there. A naive `actual / target` would report 150% and read as
 * beating the target by half when the pilot is only halfway.
 *
 * Can exceed 100%, and deliberately is not clamped here — beating a target is
 * worth knowing, and the caller clamps the bar rather than the figure.
 */
export function progressPercent(kpi: {
  baseline: number | null;
  target: number | null;
  actual: number | null;
}): number | null {
  const { baseline, target, actual } = kpi;
  if (target === null || actual === null) return null;

  const from = baseline ?? 0;
  if (target === from) return null;

  return ((actual - from) / (target - from)) * 100;
}
